import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, TabList, Tabs } from 'react-tabs';
import { dateFormat, format12Hour, format24Hour, singleDigit12HourFormat, tempTimeOptions } from '../../constants/constants';
import NoUnits from '../../images/no-units-found.svg';
import { fetchLockerWiseAvailabilityAndAppointments } from '../../redux/locker-wise-availability/actions/lockerWiseAvailability.actions';
import {
    LockerWiseAvailabilityAndAppointmentsResponse,
    LockerWiseAvailabilityAndAppointmentsState,
} from '../../redux/locker-wise-availability/interfaces/lockerWiseAvailability.interface';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import Loader from '../common/loader/Loader';
import TinyLoader from '../common/tiny-loader/TinyLoader';
import { AvailabilityRedirectionContext } from '../context/availabilityRedirection';
import styles from './appointmentDateTimePicker.module.css';
import { IAppointmentDateTimePicker, SpecificLockersData } from './interface/appointmentDateTimePicker.interface';
import PineappleSorry from '../../images/pineapple-juice.svg';

const AppointmentDateTimePicker = ({
    setSlotRange,
    slotRange,
    setEnable,
    setSelectedDate,
    selectedDate,
    selectedUnit,
    activeTab,
    setActiveTab,
    setSpecificLockerData,
    specificLockerData,
    selectedSlots,
    setSelectedSlots,
}: IAppointmentDateTimePicker) => {

    const availabilityRedirectionContext = useContext(AvailabilityRedirectionContext);
    const lockerType = availabilityRedirectionContext?.locationDetails?.lockerName || 'kiosk'
    const dispatch = useDispatch<AppDispatch>();
    const { loading, data } = useSelector((state: IRootState) => state.createAppoinmentReducer.unit);
    const lockerWiseAvailabilityAndAppointmentsData = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).data;

    const selectedLocationLockersCount = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).lockerCounts;

    const lockerWiseAvailabilityAndAppointmentLoading = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).loading;

    const vendorAppointmentData = useSelector<IRootState, LockerWiseAvailabilityAndAppointmentsState>(
        (state) => state.lockerWiseAvailabilityAndAppointments,
    ).vendorAppointments;

    const [differentUnitsAvailability, setDifferentUnitsAvailability] = useState<LockerWiseAvailabilityAndAppointmentsResponse[]>([]);
    const [diffUnitAvailabilityMessage, setDifferentUnitAvailabilityMessage] = useState('');
    const [modifiedTempTimeOptions, setModifiedTempTimeOptions] = useState<SpecificLockersData[]>([]);
    const currentSelectedDate = useMemo(() => moment(selectedDate).format(dateFormat), [selectedDate, dateFormat]);

    // Memoize unit ID to prevent unnecessary re-renders
    const unitId = useMemo(() => {
        return availabilityRedirectionContext?.availabilityData.unitId || selectedUnit;
    }, [availabilityRedirectionContext?.availabilityData.unitId, selectedUnit]);

    // Memoize sorted data to avoid re-sorting on every render
    const sortedLockerWiseAvailability = useMemo(() => {
        return [...lockerWiseAvailabilityAndAppointmentsData].sort((a, b) => {
            const dateA = new Date(a.startDate).getTime();
            const dateB = new Date(b.startDate).getTime();
            return dateA - dateB;
        });
    }, [lockerWiseAvailabilityAndAppointmentsData]);

    // Track fetched locationId to prevent duplicate API calls
    const fetchedLocationIdRef = React.useRef<string | null>(null);

    // Fetch availability data only when locationId changes, not on date/tab changes
    useEffect(() => {
        const locationId = availabilityRedirectionContext?.locationDetails.id;
        if (locationId && fetchedLocationIdRef.current !== locationId) {
            dispatch(fetchLockerWiseAvailabilityAndAppointments(locationId));
            fetchedLocationIdRef.current = locationId;
        }
        // Clear slot range when date or tab changes, but don't refetch data
        if (selectedDate || activeTab !== undefined) {
            setSlotRange('');
        }
    }, [availabilityRedirectionContext?.locationDetails.id, dispatch]);

    useEffect(() => {
        setEnable(!!slotRange);
    }, [slotRange, setEnable]);

    useEffect(() => {
        if (differentUnitsAvailability.length) {
            const temp: SpecificLockersData[] = [];

            differentUnitsAvailability.forEach((el) => {
                const [startTime] = el.openTime.split(':');
                let [endTime, endSecond] = el.closeTime.split(':');

                if (+endTime >= 23 && endSecond > '00') {
                    endTime = '24';
                    endSecond = '00';
                }

                const lastSlotHour = +endTime - 1;

                const diff = Math.max(lastSlotHour - +startTime, 0);

                for (let i = 0; i <= diff; i++) {
                    const time = moment(`${currentSelectedDate}${startTime}`, `${dateFormat} ${format24Hour}`)
                        .add(i, 'hours')
                        .format(format12Hour);
                    temp.push({
                        label: time,
                        value: time,
                        isBooked: true,
                        pmFormat: moment(time, format12Hour).format(format24Hour),
                        unitName: el.unitName,
                    });
                }
            });

            temp.sort((a, b) => (a.pmFormat < b.pmFormat ? -1 : a.pmFormat > b.pmFormat ? 1 : 0));

            setSpecificLockerData(temp);
            setDifferentUnitAvailabilityMessage('This locker has availability for a different unit');
        } else {
            setDifferentUnitAvailabilityMessage('');
        }
    }, [differentUnitsAvailability, currentSelectedDate]);

    // Memoize the data source to prevent unnecessary recalculations
    const slotData = useMemo(() => {
        return specificLockerData.length ? specificLockerData : (modifiedTempTimeOptions.length ? modifiedTempTimeOptions : tempTimeOptions);
    }, [specificLockerData, modifiedTempTimeOptions]);

    useEffect(() => {
        let slotRange = '';

        if (selectedSlots.length > 0 && slotData.length > 0) {
            const firstSlot = slotData[selectedSlots[0]];
            const lastSlot = slotData[selectedSlots[selectedSlots.length - 1]];

            if (firstSlot && lastSlot) {
                const startTime = moment(firstSlot.value, format12Hour).format(singleDigit12HourFormat);
                const endTime = moment(lastSlot.value, format12Hour).add(1, 'hours').format(singleDigit12HourFormat);

                slotRange = `${startTime} - ${endTime}`;
            }
        }
        setSlotRange(slotRange);
    }, [selectedSlots, slotData]);

    useEffect(() => {
        if (!availabilityRedirectionContext?.selectedSlotsIndex.length) {
            setSelectedSlots([]);
        }
    }, [selectedDate]);

    useEffect(() => {
        const temp: SpecificLockersData[] = [];
        const locker = activeTab + 1;
        const unit = unitId;
        const selected = moment.utc(currentSelectedDate).format('YYYY-MM-DD');

        const specificLockerWiseData = sortedLockerWiseAvailability?.filter((item) => {
            const startDate = moment.utc(item.startDate).format('YYYY-MM-DD');
            const endDate = moment.utc(item.endDate).format('YYYY-MM-DD');
            if (lockerType?.toLowerCase() === 'bms' && selected === startDate && selected === endDate && item.unitId === unit) {
                return true;
            }

            if (item.locker === locker && lockerType?.toLowerCase() === 'kiosk' && selected === startDate && selected === endDate && item.unitId === unit) {
                return true;
            }
            return false;
        });

        if (lockerType !== 'bms' && !specificLockerWiseData?.length) {
            const diffUnitsData = sortedLockerWiseAvailability?.filter((item) => {
                if (item.locker === locker && lockerType?.toLowerCase() === 'kiosk') {
                    const startDate = moment.utc(item.startDate).format('YYYY-MM-DD');
                    const endDate = moment.utc(item.endDate).format('YYYY-MM-DD');

                    if (selected === startDate && selected === endDate && item.unitId !== unit) {
                        return true;
                    }
                }
                return false;
            });

            setDifferentUnitsAvailability(diffUnitsData);
        } else {
            // Clear different units availability when we have specific locker data
            setDifferentUnitsAvailability([]);
        }

        specificLockerWiseData?.forEach((el) => {
            const [startTime] = el.openTime.split(':');
            let [endTime, endSecond] = el.closeTime.split(':');
            if (endTime == '00' && endSecond == '00') {
                endTime = '24';
                endSecond = '00';
            }
            const lastSlotHour = +endTime - 1;
            const diff = Math.max(lastSlotHour - +startTime, 0);

            for (let i = 0; i <= diff; i++) {
                const time = moment(`${currentSelectedDate}${startTime}`, `${dateFormat} ${format24Hour}`)
                    .add(i, 'hours')
                    .format(format12Hour);
                temp.push({ label: time, value: time, isBooked: false, pmFormat: moment(time, format12Hour).format(format24Hour) });
            }
        });

        temp.sort((a, b) => (a.pmFormat < b.pmFormat ? -1 : a.pmFormat > b.pmFormat ? 1 : 0));
        const selectedDateStr = moment(currentSelectedDate).format('YYYY-MM-DD');
        for (const appointment of specificLockerWiseData) {
            appointment?.appointments?.forEach((app) => {
                for (let i = 0; i < temp.length; i++) {
                    const slot = temp[i];
                    const appointmentStartTime = moment(app.startTime, singleDigit12HourFormat);
                    const appointmentEndTime = moment(app.endTime, singleDigit12HourFormat);
                    const appointmentDate = moment(app.reservedDate).format('YYYY-MM-DD');
                    const isBetweenAppointmentRange =
                        moment(slot.value, format12Hour).isBetween(appointmentStartTime, appointmentEndTime, undefined, '[]') &&
                        appointmentDate === selectedDateStr;

                    if (isBetweenAppointmentRange) {
                        slot.isBooked = true;

                        if (
                            i > 0 &&
                            moment(temp[i - 1].value, format12Hour).format(format12Hour) ===
                            moment(slot.value, format12Hour).subtract(1, 'hours').format(format12Hour)
                        ) {
                            temp[i - 1].isBooked = true;
                        }
                    }
                }
            });
        }

        // ✅ Vendor appointments blocking
        // for (const vendorApp of vendorAppointmentData || []) {
        //     const vendorStartDate = moment.utc(vendorApp.startDate).format("YYYY-MM-DD");
        //     const vendorEndDate = moment.utc(vendorApp.endDate).format("YYYY-MM-DD");
        //     const selectedDateStr = moment(currentSelectedDate).format("YYYY-MM-DD");

        //     const isWithinDateRange =
        //         moment(selectedDateStr).isSameOrAfter(vendorStartDate) &&
        //         moment(selectedDateStr).isSameOrBefore(vendorEndDate);

        //     // Check locker match based on locker type
        //     let shouldBlockLocker = false;
        //     if (lockerType?.toLowerCase() === 'bms') {
        //         // For BMS, check if vendor locker is in the lockerNumber array
        //         const lockerNumbers = availabilityRedirectionContext?.locationDetails?.lockerNumber || [];
        //         shouldBlockLocker = lockerNumbers.includes(String(vendorApp.locker));
        //     } else {
        //         // For kiosk, check if vendor locker matches current locker
        //         shouldBlockLocker = vendorApp.locker === locker;
        //     }

        //     if (isWithinDateRange && shouldBlockLocker) {
        //         if (vendorApp.allowEntireTimeBlock) {
        //             // For allowEntireTimeBlock, check if selected date is within the exact date range
        //             const isExactDateMatch =
        //                 moment(selectedDateStr).isSame(vendorStartDate) ||
        //                 moment(selectedDateStr).isSame(vendorEndDate) ||
        //                 moment(selectedDateStr).isBetween(vendorStartDate, vendorEndDate, 'day', '[]');

        //             if (isExactDateMatch) {
        //                 // For same day as start date, check if current time is after start time
        //                 if (moment(selectedDateStr).isSame(vendorStartDate)) {
        //                     const vendorStartTime = moment(vendorApp.startTime, singleDigit12HourFormat);
        //                     for (let i = 0; i < temp.length; i++) {
        //                         const slot = temp[i];
        //                         const slotTime = moment(slot.value, format12Hour);
        //                         if (slotTime.isSameOrAfter(vendorStartTime)) {
        //                             temp[i].isBooked = true;
        //                         }
        //                     }
        //                 }
        //                 // For same day as end date, check if current time is before end time
        //                 else if (moment(selectedDateStr).isSame(vendorEndDate)) {
        //                     const vendorEndTime = moment(vendorApp.endTime, singleDigit12HourFormat);
        //                     for (let i = 0; i < temp.length; i++) {
        //                         const slot = temp[i];
        //                         const slotTime = moment(slot.value, format12Hour);
        //                         if (slotTime.isSameOrBefore(vendorEndTime)) {
        //                             temp[i].isBooked = true;
        //                         }
        //                     }
        //                 }
        //                 // For dates between start and end, block all slots
        //                 else {
        //                     for (let i = 0; i < temp.length; i++) {
        //                         temp[i].isBooked = true;
        //                     }
        //                 }
        //             }
        //         }

        //         if (vendorApp.allowStratEndTimeEachDay) {
        //             // ⏰ Block between vendor start and end time + next hour slot
        //             const vendorStartTime = moment(vendorApp.startTime, singleDigit12HourFormat);
        //             const vendorEndTime = moment(vendorApp.endTime, singleDigit12HourFormat);
        //             const vendorEndTimePlusOne = moment(vendorApp.endTime, singleDigit12HourFormat).add(1, 'hour');

        //             for (let i = 0; i < temp.length; i++) {
        //                 const slot = temp[i];
        //                 const slotTime = moment(slot.value, format12Hour);

        //                 // Check if slot time is within vendor time range (inclusive start, exclusive end + 1 hour)
        //                 const isWithinVendorTime = slotTime.isSameOrAfter(vendorStartTime) &&
        //                     slotTime.isBefore(vendorEndTimePlusOne);

        //                 if (isWithinVendorTime) {
        //                     slot.isBooked = true;
        //                 }
        //             }
        //         }
        //     }
        // }

        setSpecificLockerData(temp);
    }, [sortedLockerWiseAvailability, activeTab, currentSelectedDate, unitId, lockerType, vendorAppointmentData]);

    // ✅ Apply vendor appointment blocking to tempTimeOptions when no specific locker data
    // useEffect(() => {
    //     if (!specificLockerData.length && tempTimeOptions.length > 0) {
    //         const tempTimeOptionsWithVendorBlocking = tempTimeOptions.map((option: any) => ({ ...option, isBooked: false }));
    //         const selectedDateStr = moment(currentSelectedDate).format("YYYY-MM-DD");

    //         for (const vendorApp of vendorAppointmentData || []) {
    //             const vendorStartDate = moment.utc(vendorApp.startDate).format("YYYY-MM-DD");
    //             const vendorEndDate = moment.utc(vendorApp.endDate).format("YYYY-MM-DD");

    //             const isWithinDateRange =
    //                 moment(selectedDateStr).isSameOrAfter(vendorStartDate) &&
    //                 moment(selectedDateStr).isSameOrBefore(vendorEndDate);

    //             // Check locker match based on locker type for tempTimeOptions
    //             let shouldBlockLocker = false;
    //             if (lockerType?.toLowerCase() === 'bms') {
    //                 // For BMS, check if vendor locker is in the lockerNumber array
    //                 const lockerNumbers = availabilityRedirectionContext?.locationDetails?.lockerNumber || [];
    //                 shouldBlockLocker = lockerNumbers.includes(String(vendorApp.locker));
    //             } else {
    //                 // For kiosk, check if vendor locker matches current locker (activeTab + 1)
    //                 shouldBlockLocker = vendorApp.locker === (activeTab + 1);
    //             }

    //             if (isWithinDateRange && shouldBlockLocker) {
    //                 if (vendorApp.allowEntireTimeBlock) {
    //                     // For allowEntireTimeBlock, check if selected date is within the exact date range
    //                     const isExactDateMatch =
    //                         moment(selectedDateStr).isSame(vendorStartDate) ||
    //                         moment(selectedDateStr).isSame(vendorEndDate) ||
    //                         moment(selectedDateStr).isBetween(vendorStartDate, vendorEndDate, 'day', '[]');

    //                     if (isExactDateMatch) {
    //                         // For same day as start date, check if current time is after start time
    //                         if (moment(selectedDateStr).isSame(vendorStartDate)) {
    //                             const vendorStartTime = moment(vendorApp.startTime, singleDigit12HourFormat);
    //                             for (let i = 0; i < tempTimeOptionsWithVendorBlocking.length; i++) {
    //                                 const slot = tempTimeOptionsWithVendorBlocking[i];
    //                                 const slotTime = moment(slot.value, format12Hour);
    //                                 if (slotTime.isSameOrAfter(vendorStartTime)) {
    //                                     tempTimeOptionsWithVendorBlocking[i].isBooked = true;
    //                                 }
    //                             }
    //                         }
    //                         // For same day as end date, check if current time is before end time
    //                         else if (moment(selectedDateStr).isSame(vendorEndDate)) {
    //                             const vendorEndTime = moment(vendorApp.endTime, singleDigit12HourFormat);
    //                             for (let i = 0; i < tempTimeOptionsWithVendorBlocking.length; i++) {
    //                                 const slot = tempTimeOptionsWithVendorBlocking[i];
    //                                 const slotTime = moment(slot.value, format12Hour);
    //                                 if (slotTime.isSameOrBefore(vendorEndTime)) {
    //                                     tempTimeOptionsWithVendorBlocking[i].isBooked = true;
    //                                 }
    //                             }
    //                         }
    //                         // For dates between start and end, block all slots
    //                         else {
    //                             for (let i = 0; i < tempTimeOptionsWithVendorBlocking.length; i++) {
    //                                 tempTimeOptionsWithVendorBlocking[i].isBooked = true;
    //                             }
    //                         }
    //                     }
    //                 }

    //                 if (vendorApp.allowStratEndTimeEachDay) {
    //                     // ⏰ Block between vendor start and end time + next hour slot in tempTimeOptions
    //                     const vendorStartTime = moment(vendorApp.startTime, singleDigit12HourFormat);
    //                     const vendorEndTime = moment(vendorApp.endTime, singleDigit12HourFormat);
    //                     const vendorEndTimePlusOne = moment(vendorApp.endTime, singleDigit12HourFormat).add(1, 'hour');

    //                     for (let i = 0; i < tempTimeOptionsWithVendorBlocking.length; i++) {
    //                         const slot = tempTimeOptionsWithVendorBlocking[i];
    //                         const slotTime = moment(slot.value, format12Hour);

    //                         // Check if slot time is within vendor time range (inclusive start, exclusive end + 1 hour)
    //                         const isWithinVendorTime = slotTime.isSameOrAfter(vendorStartTime) &&
    //                             slotTime.isBefore(vendorEndTimePlusOne);

    //                         if (isWithinVendorTime) {
    //                             slot.isBooked = true;
    //                         }
    //                     }
    //                 }
    //             }
    //         }

    //         // Update state with vendor blocking applied
    //         setModifiedTempTimeOptions(tempTimeOptionsWithVendorBlocking);
    //     } else {
    //         // Reset when specific locker data is available
    //         setModifiedTempTimeOptions([]);
    //     }
    // }, [specificLockerData.length, tempTimeOptions, currentSelectedDate, vendorAppointmentData, activeTab]);

    const onChange = (date: Date) => {
        setSelectedDate(date);
        setSelectedSlots([]);
    };

    const selectSlotHandler = (index: number) => {
        const timeFormat = specificLockerData.length ? format12Hour : format24Hour;
        const data = specificLockerData.length ? specificLockerData : (modifiedTempTimeOptions.length ? modifiedTempTimeOptions : tempTimeOptions);

        if (selectedSlots.length === 0) {
            setSelectedSlots([index]);
        } else {
            const lastSelectedSlot = selectedSlots[selectedSlots.length - 1];

            if (
                index === lastSelectedSlot + 1 &&
                data[index].value === moment(`${data[lastSelectedSlot].value}`, timeFormat).add(1, 'hours').format(timeFormat)
            ) {
                setSelectedSlots([...selectedSlots, index]);
            } else if (
                index === selectedSlots[0] - 1 &&
                data[selectedSlots[0]].value === moment(`${data[index].value}`, timeFormat).add(1, 'hours').format(timeFormat)
            ) {
                setSelectedSlots([index, ...selectedSlots]);
            } else {
                setSelectedSlots([index]);
            }
        }
    };

    const areAllSlotsPastForToday = (slots: SpecificLockersData[], date: Date): boolean => {
        const now = new Date();
        return slots.every((slot) => {
            const [hour, minute] = slot.pmFormat.split(':').map(Number);
            const slotDate = new Date(date);
            slotDate.setHours(hour, minute, 0, 0);
            // Only count as past if slot start time is strictly less than current hour and minute
            return (
                slotDate.getTime() < now.getTime() &&
                (slotDate.getHours() < now.getHours() ||
                    (slotDate.getHours() === now.getHours() && slotDate.getMinutes() < now.getMinutes()))
            );
        });
    };

    return loading ? (
        <Loader />
    ) : (
        <div className={`${styles.dateTimePickerContainer} date-time-picker`}>
            <div className={`${styles.calendarWrapper} calendar-wrapper`}>
                <Calendar
                    minDate={new Date(moment().format())}
                    onChange={onChange}
                    next2Label={null}
                    selectRange={false}
                    prev2Label={null}
                    allowPartialRange={true}
                    value={selectedDate}
                />
            </div>
            <div className={styles.slotWrapper}>
                <div className={styles.lockerTitleWrapper}>
                    <h2>
                        Select consecutive time slots (one or more) for the visit of apartment{' '}
                        {data?.unitYardiId ? data?.unitYardiId : data?.name} on <span>{moment(selectedDate).format('MM-DD-YYYY')}</span>
                    </h2>

                    {lockerType !== 'bms' && (
                        <div className={`${styles.tabWrapper} tabWrapper`}>
                            <Tabs
                                selectedIndex={activeTab}
                                onSelect={(index) => {
                                    setActiveTab(index);
                                    setSelectedSlots([]);
                                }}>
                                <TabList>
                                    {Array(selectedLocationLockersCount)
                                        .fill('')
                                        .map((locker, index) => (
                                            <Tab key={index}>Locker {index + 1}</Tab>
                                        ))}
                                </TabList>
                            </Tabs>
                        </div>
                    )}
                </div>

                {lockerWiseAvailabilityAndAppointmentLoading && !specificLockerData.length ? (
                    <div className={styles.specificLockerLoader}>
                        <TinyLoader />
                    </div>
                ) : (
                    <div
                        className={`${styles.slotListing} ${specificLockerData.length > 0 || tempTimeOptions?.length > 0 || modifiedTempTimeOptions.length > 0 ? '' : styles.noSlotsContainer
                            }`}>
                        {(tempTimeOptions && tempTimeOptions.length > 0) || (specificLockerData && specificLockerData.length > 0) || (modifiedTempTimeOptions && modifiedTempTimeOptions.length > 0) ? (
                            (specificLockerData.length ? specificLockerData : (modifiedTempTimeOptions.length ? modifiedTempTimeOptions : tempTimeOptions)).map(
                                (option: { value: string; isBooked?: boolean; unitName?: string }, index: number) => {
                                    const isSelected = selectedSlots.includes(index);
                                    const currentHour = moment().format('h A');

                                    const startTime = moment(option.value, singleDigit12HourFormat).startOf('hour');
                                    const todaysDate = moment().format(dateFormat);

                                    if (
                                        moment(selectedDate).format(dateFormat) === todaysDate &&
                                        startTime.isSameOrAfter(moment(currentHour, 'h A'))
                                    ) {
                                        return (
                                            <div
                                                onClick={() => !option.isBooked && selectSlotHandler(index)}
                                                key={index}
                                                className={
                                                    option.isBooked
                                                        ? styles.bookedSlot
                                                        : `${styles.slotItem} ${isSelected ? styles.selected : ''}`
                                                }>
                                                <div>
                                                    <span>{startTime.format('h A')} - </span>
                                                    <span>&nbsp;{startTime.add(1, 'hours').format('h A')}</span>
                                                </div>
                                                {option.unitName && <p className={styles.unitName}>{option.unitName}</p>}
                                            </div>
                                        );
                                    } else {
                                        return (
                                            moment(selectedDate).format(dateFormat) !== todaysDate && (
                                                <div
                                                    onClick={() => !option.isBooked && selectSlotHandler(index)}
                                                    key={index}
                                                    className={
                                                        option.isBooked
                                                            ? styles.bookedSlot
                                                            : `${styles.slotItem} ${isSelected ? styles.selected : ''} `
                                                    }>
                                                    <div>
                                                        <span>{moment(option.value, singleDigit12HourFormat).format('h A')} - </span>
                                                        <span>
                                                            &nbsp;
                                                            {moment(option.value, singleDigit12HourFormat).add(1, 'hours').format('h A')}
                                                        </span>
                                                    </div>
                                                    {option.unitName && <p className={styles.unitName}>{option.unitName}</p>}
                                                </div>
                                            )
                                        );
                                    }
                                },
                            )
                        ) : (
                            <div className={styles.noUnits}>
                                <img src={NoUnits} alt="no-units" />
                                <p className={styles.noUnitsFoundText}>No Slots Available</p>
                            </div>
                        )}
                    </div>
                )}

                {specificLockerData &&
                    specificLockerData.length > 0 &&
                    moment(selectedDate).isSame(moment(), 'day') &&
                    areAllSlotsPastForToday(specificLockerData, selectedDate) && (
                        <div className={styles.noSlots}>
                            <img src={PineappleSorry} alt="sorry" width={200} height={200} />
                            <div className={styles.noSlotsInfo}>
                                <p className={styles.noSlotsText}>No available slots for appointments today.</p>
                                <p className={styles.noSlotsDescriptionText}>Please select another date to book an appointment.</p>
                            </div>
                        </div>
                    )}
                {!specificLockerData.length && !loading && !lockerWiseAvailabilityAndAppointmentLoading ? (
                    <div className={styles.instruction}>
                        <span>
                            No availability already set. Select times to create availability for an appointment and that appointment
                            together
                        </span>
                    </div>
                ) : (
                    !!specificLockerData.length && (
                        (!areAllSlotsPastForToday(specificLockerData, selectedDate) || diffUnitAvailabilityMessage) && <div className={styles.instruction}>
                            <div className={styles.colorBox}></div>
                            <span>
                                {diffUnitAvailabilityMessage
                                    ? diffUnitAvailabilityMessage
                                    : 'Appointment is already scheduled for that time'}
                            </span>
                        </div>
                    )
                )}
                {!!selectedSlots.length && !lockerWiseAvailabilityAndAppointmentLoading && (
                    <div className={styles.selectedSlot}>
                        <h2>Selected Timeslots</h2>
                        <div>
                            <span>{slotRange}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppointmentDateTimePicker;
